import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Select, Input, Badge, Spin, message, Avatar, Tooltip } from 'antd';
import {
  TeamOutlined, PlusOutlined, SendOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { useSocket } from '../../context/SocketContext';
import {
  getContacts, getConversations, getOrCreateDirect,
  createGroup, getMessages, sendMessage, markRead,
} from './service';
import './Chat.css';

const { Option } = Select;

// ── Utility: format timestamp ─────────────────────────────────────────────────
function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// ── Current user id from localStorage JWT ────────────────────────────────────
function getCurrentUserId() {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id;
  } catch (_) {
    return null;
  }
}

// ── Message row ───────────────────────────────────────────────────────────────
function MsgRow({ msg, myId, isGroup }) {
  const isMine = Number(msg.sender_id) === Number(myId);
  return (
    <div className={`msg-row${isMine ? ' mine' : ''}`}>
      {!isMine && (
        <Avatar className="msg-avatar" style={{ background: '#1890ff', flexShrink: 0 }}>
          {initials(msg.sender_name)}
        </Avatar>
      )}
      <div className="msg-bubble-wrap">
        {!isMine && isGroup && (
          <div className="msg-sender">{msg.sender_name}</div>
        )}
        <div className="msg-bubble">{msg.message}</div>
        <div className="msg-time">{formatTime(msg.created_at)}</div>
      </div>
    </div>
  );
}

// ── Main Chat Component ───────────────────────────────────────────────────────
export default function Chat() {
  const myId = getCurrentUserId();
  // Use the single shared socket from context (no duplicate connection)
  const { socket, onlineUsers, setActiveConvId } = useSocket();

  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);

  // Modals
  const [newChatVisible, setNewChatVisible] = useState(false);
  const [newGroupVisible, setNewGroupVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);

  const messagesEndRef = useRef(null);

  // ── Listen for new messages via shared socket ──────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg) => {
      if (Number(msg.sender_id) === Number(getCurrentUserId())) return;
      setActiveConv(prev => {
        if (prev && prev.id === msg.conversation_id) {
          setMessages(old => [...old, msg]);
          markRead(msg.conversation_id).catch(() => {});
        }
        return prev;
      });
      loadConversations();
    };

    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Load conversations ─────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const data = await getConversations();
      setConversations(data.conversations || []);
    } catch (err) {
      message.error('Failed to load conversations');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  // ── Load contacts ──────────────────────────────────────────────────────────
  const loadContacts = useCallback(async () => {
    try {
      const data = await getContacts();
      setContacts(data.contacts || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadConversations();
    loadContacts();
    return () => setActiveConvId(null); // clear when leaving chat page
  }, [loadConversations, loadContacts, setActiveConvId]);

  // ── Open conversation ──────────────────────────────────────────────────────
  const openConversation = async (conv) => {
    setActiveConv(conv);
    setActiveConvId(conv.id); // tell context so popup suppresses for this conv
    setMessages([]);
    setLoadingMsgs(true);

    if (socket) socket.emit('join_conversation', conv.id);

    try {
      const data = await getMessages(conv.id);
      setMessages(data.messages || []);
      await markRead(conv.id);
      // Clear unread badge locally
      setConversations(prev =>
        prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c)
      );
    } catch (err) {
      message.error('Failed to load messages');
    } finally {
      setLoadingMsgs(false);
    }
  };

  // ── Send message ───────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!inputText.trim() || !activeConv || sending) return;
    const text = inputText.trim();
    setInputText('');
    setSending(true);
    try {
      const data = await sendMessage(activeConv.id, text);
      // Optimistically add (socket will also emit to others; add only if sender)
      setMessages(prev => [...prev, data.message]);
      loadConversations();
    } catch (err) {
      message.error('Failed to send message');
      setInputText(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Start direct chat ──────────────────────────────────────────────────────
  const handleStartDirect = async () => {
    if (!selectedContact) return;
    try {
      const data = await getOrCreateDirect(selectedContact);
      setNewChatVisible(false);
      setSelectedContact(null);
      await loadConversations();
      // Find and open the conversation
      const allConvs = (await getConversations()).conversations || [];
      const conv = allConvs.find(c => c.id === data.conversation_id);
      if (conv) openConversation(conv);
    } catch (err) {
      message.error('Failed to start chat');
    }
  };

  // ── Create group ───────────────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) {
      message.warning('Provide a group name and at least one member');
      return;
    }
    try {
      const data = await createGroup(groupName.trim(), groupMembers);
      setNewGroupVisible(false);
      setGroupName('');
      setGroupMembers([]);
      await loadConversations();
      const allConvs = (await getConversations()).conversations || [];
      const conv = allConvs.find(c => c.id === data.conversation_id);
      if (conv) openConversation(conv);
    } catch (err) {
      message.error('Failed to create group');
    }
  };

  // ── Render conversation name ───────────────────────────────────────────────
  function convName(conv) {
    if (conv.type === 'group') return conv.group_name || 'Group';
    return conv.other_name || 'Unknown';
  }

  // ── Date dividers ──────────────────────────────────────────────────────────
  function renderMessages() {
    const result = [];
    let lastDate = null;
    messages.forEach((msg, i) => {
      const d = formatDate(msg.created_at);
      if (d !== lastDate) {
        result.push(
          <div key={`div-${i}`} className="msg-date-divider">{d}</div>
        );
        lastDate = d;
      }
      result.push(
        <MsgRow
          key={msg.id}
          msg={msg}
          myId={myId}
          isGroup={activeConv && activeConv.type === 'group'}
        />
      );
    });
    return result;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="chat-container">
      {/* ── Sidebar ── */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h3>Chat</h3>
          <div className="chat-sidebar-actions">
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setNewChatVisible(true)}
            >
              New Chat
            </Button>
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => setNewGroupVisible(true)}
            >
              New Group
            </Button>
          </div>
        </div>

        <div className="chat-conv-list">
          {loadingConvs ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin size="small" />
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#bfbfbf', fontSize: 13 }}>
              No conversations yet
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                className={`chat-conv-item${activeConv && activeConv.id === conv.id ? ' active' : ''}`}
                onClick={() => openConversation(conv)}
              >
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div className={`chat-conv-avatar${conv.type === 'group' ? ' group' : ''}`}>
                    {conv.type === 'group' ? <TeamOutlined /> : initials(conv.other_name || '?')}
                  </div>
                  {conv.type === 'direct' && (
                    <span style={{
                      position: 'absolute', bottom: 0, right: 0,
                      width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff',
                      background: onlineUsers.has(Number(conv.other_id)) ? '#22c55e' : '#d1d5db',
                    }} />
                  )}
                </div>
                <div className="chat-conv-info">
                  <div className="chat-conv-name">{convName(conv)}</div>
                  <div className="chat-conv-preview">
                    {conv.last_message || 'No messages yet'}
                  </div>
                </div>
                <div className="chat-conv-meta">
                  <span className="chat-conv-time">
                    {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                  </span>
                  {conv.unread_count > 0 && (
                    <Badge count={conv.unread_count} size="small" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Main area ── */}
      <div className="chat-main">
        {!activeConv ? (
          <div className="chat-empty">
            <WechatOutlined />
            <p>Select a conversation or start a new chat</p>
          </div>
        ) : (
          <>
            <div className="chat-main-header">
              <div className={`chat-conv-avatar${activeConv.type === 'group' ? ' group' : ''}`}
                   style={{ width: 36, height: 36, fontSize: 14, borderRadius: '50%',
                            background: activeConv.type === 'group' ? '#52c41a' : '#1890ff',
                            color: '#fff', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontWeight: 700 }}>
                {activeConv.type === 'group'
                  ? <TeamOutlined />
                  : initials(activeConv.other_name || '?')
                }
              </div>
              <div>
                <h4 style={{ margin: 0 }}>{convName(activeConv)}</h4>
                <div className="chat-main-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {activeConv.type === 'direct' ? (
                    <>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                        background: onlineUsers.has(Number(activeConv.other_id)) ? '#22c55e' : '#d1d5db',
                      }} />
                      <span style={{ color: onlineUsers.has(Number(activeConv.other_id)) ? '#22c55e' : '#9ca3af', fontSize: 12 }}>
                        {onlineUsers.has(Number(activeConv.other_id)) ? 'Online' : 'Offline'}
                      </span>
                    </>
                  ) : 'Group conversation'}
                </div>
              </div>
            </div>

            <div className="chat-messages">
              {loadingMsgs ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin />
                </div>
              ) : (
                renderMessages()
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              <textarea
                rows={1}
                placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
              />
              <Tooltip title="Send">
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={sending}
                  disabled={!inputText.trim()}
                />
              </Tooltip>
            </div>
          </>
        )}
      </div>

      {/* ── New Direct Chat Modal ── */}
      <Modal
        title="Start New Chat"
        open={newChatVisible}
        onOk={handleStartDirect}
        onCancel={() => { setNewChatVisible(false); setSelectedContact(null); }}
        okText="Start Chat"
        okButtonProps={{ disabled: !selectedContact }}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Select a contact:</div>
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Search staff members"
          optionFilterProp="children"
          value={selectedContact}
          onChange={val => setSelectedContact(val)}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {contacts.map(c => (
            <Option key={c.id} value={c.id}>{c.name}</Option>
          ))}
        </Select>
      </Modal>

      {/* ── New Group Modal ── */}
      <Modal
        title="Create Group Chat"
        open={newGroupVisible}
        onOk={handleCreateGroup}
        onCancel={() => { setNewGroupVisible(false); setGroupName(''); setGroupMembers([]); }}
        okText="Create Group"
        okButtonProps={{ disabled: !groupName.trim() || groupMembers.length === 0 }}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Group name:</div>
        <Input
          placeholder="e.g. HR Team"
          value={groupName}
          onChange={e => setGroupName(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Add members:</div>
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Select staff members"
          optionFilterProp="children"
          value={groupMembers}
          onChange={vals => setGroupMembers(vals)}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          {contacts.map(c => (
            <Option key={c.id} value={c.id}>{c.name}</Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
